<!--
/**
 * FormKitOptionsMultiSelect
 *
 * Multi-select for flat `{ value, label }[]` options using PrimeVue MultiSelect
 * with the formkit-primevue wrapper so CardField width constraints apply.
 */
-->
<template>
  <div v-bind="attrs" :aria-required="isRequired" :class="wrapperClass">
    <MultiSelect
      :id="inputId"
      :model-value="localValue"
      :options="options"
      option-label="label"
      option-value="value"
      :placeholder="displayPlaceholder"
      :filter="filter"
      filter-placeholder="Search"
      display="chip"
      :disabled="disabled"
      :aria-label="ariaLabel"
      class="w-full min-w-0 max-w-full"
      data-testid="formkit-options-multi-select"
      :show-clear="showClear"
      @update:model-value="onInput"
    />
  </div>
</template>

<script lang="ts" setup>
import type { FormKitFrameworkContext } from '@formkit/core'
import type { PropType } from 'vue'
import { computed } from 'vue'
import { useFormKitSelect } from '~/composables/useFormKitSelect'

type SelectOption = { value: string; label: string }

const props = defineProps({
  context: {
    type: Object as PropType<
      FormKitFrameworkContext & {
        placeholder?: string
        filter?: boolean
        disabled?: boolean
        showClear?: boolean
        options?: SelectOption[]
        ariaLabel?: string
      }
    >,
    default: undefined,
  },
  modelValue: {
    type: Array as PropType<string[] | null>,
    default: undefined,
  },
  placeholder: { type: String, default: '' },
  filter: { type: Boolean, default: false },
  showClear: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  options: {
    type: Array as PropType<SelectOption[]>,
    default: () => [],
  },
  ariaLabel: { type: String, default: 'Multi selection' },
})

const emit = defineEmits<{
  'update:modelValue': [value: string[] | string | null]
}>()

const options = computed(() => props.context?.options ?? props.options ?? [])

const ariaLabel = computed(() => props.context?.ariaLabel ?? props.ariaLabel)

const {
  attrs,
  inputId,
  disabled,
  filter,
  showClear,
  isRequired,
  displayPlaceholder,
  localValue,
  onInput,
  wrapperClass,
} = useFormKitSelect({
  props: {
    ...props,
    taglist: true,
  },
  emit,
})
</script>
